<script setup>
import { Head, useForm } from '@inertiajs/vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppLayout from '../../Layouts/AppLayout.vue'

const props = defineProps({
  invitation: { type: Object, required: true },
  signedUrl: { type: String, required: true },
  // RED-286: with eID on, step 1 is proving who you are; the form below only
  // opens once SK has answered and the isikukood matched the invitation.
  eidRequired: { type: Boolean, default: false },
  eidUrls: { type: Object, default: null },
  verifiedName: { type: String, default: null },
  error: { type: String, default: null },
})

const { t } = useI18n()

const eidPending = computed(() => props.eidRequired && !props.verifiedName)

const form = useForm({
  firstname: '',
  lastname: '',
  phone: '',
  password: '',
  password_confirmation: '',
  profession_level: '',
  profession_no: '',
  land_appraiser: '',
  is_member_ekhy: false,
  is_member_ekmk: false,
})

const submit = () => {
  form.post(props.signedUrl, {
    preserveScroll: true,
  })
}

const expiresFormatted = computed(() => {
  if (!props.invitation.expires_at) return ''
  return new Date(props.invitation.expires_at).toLocaleString('et-EE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
})

const professionLevelOptions = [
  { label: 'Vara hindaja, tase 7', value: 'Vara hindaja, tase 7' },
  { label: 'Kinnisvara hindaja, tase 6', value: 'Kinnisvara hindaja, tase 6' },
  { label: 'Nooremhindaja, tase 5', value: 'Nooremhindaja, tase 5' },
  { label: 'Hindaja', value: 'Hindaja' },
  { label: 'Kinnisvaramaakler, tase 5', value: 'Kinnisvaramaakler, tase 5' },
  { label: 'Kinnisvara vanemmaakler, tase 6', value: 'Kinnisvara vanemmaakler, tase 6' },
  { label: 'Maakler', value: 'Maakler' },
  { label: '-', value: '-' },
]
</script>

<template>
  <Head :title="t('messages.invitations.accept.page_title')" />

  <AppLayout>
    <div class="flex flex-col w-full mt-6 mb-12">
      <div class="flex justify-center">
        <div class="max-w-xl w-full flex flex-col gap-4">
          <!-- Invitation context -->
          <Card>
            <template #content>
              <div class="flex flex-col gap-2">
                <h1 class="text-xl font-medium text-gray-900 m-0 leading-snug">
                  {{ t('messages.invitations.accept.page_title') }}
                </h1>

                <p class="text-sm text-gray-600 m-0 leading-relaxed">
                  {{
                    t('messages.invitations.accept.invitation_context', {
                      inviter: invitation.inviter_name || t('messages.invitations.fallback_inviter'),
                      company: invitation.company_name,
                      role: invitation.role_label,
                    })
                  }}
                </p>

                <p class="text-xs text-gray-500 m-0 italic">
                  {{ t('messages.invitations.accept.invitation_expires', { date: expiresFormatted }) }}
                </p>

                <div class="text-xs text-gray-500 mt-1">
                  <span class="font-medium">{{ t('messages.Email') }}:</span>
                  {{ invitation.email }}
                </div>
              </div>
            </template>
          </Card>

          <p v-if="error" class="text-sm text-red-600 m-0" role="alert" data-testid="accept-error">
            {{ error }}
          </p>

          <!-- Step 1: prove who you are -->
          <Card v-if="eidPending">
            <template #content>
              <div class="flex flex-col gap-3">
                <h2 class="text-lg font-medium text-gray-900 m-0">
                  {{ t('messages.eid.invite_heading') }}
                </h2>
                <p class="text-sm text-gray-600 m-0 leading-relaxed">
                  {{ t('messages.eid.invite_intro') }}
                </p>
                <div class="flex flex-col gap-2 mt-1">
                  <a
                    v-for="(url, method) in eidUrls || {}"
                    :key="method"
                    :href="url"
                    :data-testid="`accept-eid-${method}`"
                    class="w-full text-center bg-white border border-gray-300 rounded-md py-2 px-4 text-sm font-medium text-gray-800 hover:bg-gray-50"
                  >
                    {{ t(`messages.eid.${method}`) }}
                  </a>
                </div>
              </div>
            </template>
          </Card>

          <!-- Registration form -->
          <Card v-if="!eidPending">
            <template #content>
              <Fluid>
                <form class="flex flex-col gap-3" @submit.prevent="submit">
                  <!-- Name comes from the ID document once eID has answered. -->
                  <div v-if="verifiedName">
                    <label class="text-sm text-gray-600 block mb-1">
                      {{ t('messages.eid.verified') }}
                    </label>
                    <InputText :model-value="verifiedName" readonly size="small" data-testid="accept-verified-name" />
                    <small class="text-gray-500">{{ t('messages.eid.verified_name') }}</small>
                  </div>

                  <template v-else>
                    <div>
                      <label for="firstname" class="text-sm text-gray-600 block mb-1">
                        {{ t('messages.FirstName') }}
                      </label>
                      <InputText
                        id="firstname"
                        autocomplete="given-name"
                        v-model="form.firstname"
                        :invalid="!!form.errors.firstname"
                        autofocus
                        size="small"
                        data-testid="accept-firstname"
                      />
                      <small v-if="form.errors.firstname" class="text-red-600">
                        {{ form.errors.firstname }}
                      </small>
                    </div>

                    <div>
                      <label for="lastname" class="text-sm text-gray-600 block mb-1">
                        {{ t('messages.LastName') }}
                      </label>
                      <InputText
                        id="lastname"
                        autocomplete="family-name"
                        v-model="form.lastname"
                        :invalid="!!form.errors.lastname"
                        size="small"
                        data-testid="accept-lastname"
                      />
                      <small v-if="form.errors.lastname" class="text-red-600">
                        {{ form.errors.lastname }}
                      </small>
                    </div>
                  </template>

                  <div>
                    <label for="phone" class="text-sm text-gray-600 block mb-1">
                      {{ t('messages.phone') }}
                    </label>
                    <InputText
                      id="phone"
                      autocomplete="tel"
                      v-model="form.phone"
                      :invalid="!!form.errors.phone"
                      size="small"
                      data-testid="accept-phone"
                    />
                    <small v-if="form.errors.phone" class="text-red-600">
                      {{ form.errors.phone }}
                    </small>
                  </div>

                  <div>
                    <label for="password" class="text-sm text-gray-600 block mb-1">
                      {{ t('messages.Password1') }}
                    </label>
                    <InputText
                      id="password"
                      autocomplete="new-password"
                      v-model="form.password"
                      type="password"
                      :invalid="!!form.errors.password"
                      size="small"
                      data-testid="accept-password"
                    />
                    <small v-if="form.errors.password" class="text-red-600">
                      {{ form.errors.password }}
                    </small>
                  </div>

                  <div>
                    <label for="password_confirmation" class="text-sm text-gray-600 block mb-1">
                      {{ t('messages.Password2') }}
                    </label>
                    <InputText
                      id="password_confirmation"
                      autocomplete="new-password"
                      v-model="form.password_confirmation"
                      type="password"
                      :invalid="!!form.errors.password_confirmation"
                      size="small"
                      data-testid="accept-password-confirm"
                    />
                  </div>

                  <div>
                    <label for="profession_level" class="text-sm text-gray-600 block mb-1">
                      {{ t('messages.professionLevel') }}
                    </label>
                    <Select
                      id="profession_level"
                      v-model="form.profession_level"
                      :options="professionLevelOptions"
                      option-label="label"
                      option-value="value"
                      :invalid="!!form.errors.profession_level"
                    />
                  </div>

                  <div>
                    <label for="profession_no" class="text-sm text-gray-600 block mb-1">
                      {{ t('messages.professionNo') }}
                    </label>
                    <InputText
                      id="profession_no"
                      autocomplete="off"
                      v-model="form.profession_no"
                      :invalid="!!form.errors.profession_no"
                      size="small"
                    />
                  </div>

                  <div class="flex items-center gap-2 mt-1">
                    <Checkbox v-model="form.is_member_ekhy" binary input-id="is_member_ekhy" />
                    <label for="is_member_ekhy" class="text-sm">
                      {{ t('messages.isMember.ekhy') }}
                    </label>
                  </div>

                  <div class="flex items-center gap-2">
                    <Checkbox v-model="form.is_member_ekmk" binary input-id="is_member_ekmk" />
                    <label for="is_member_ekmk" class="text-sm">
                      {{ t('messages.isMember.ekmk') }}
                    </label>
                  </div>

                  <div class="flex justify-end mt-2">
                    <Button
                      type="submit"
                      :loading="form.processing"
                      :disabled="form.processing"
                      class="w-auto!"
                      data-testid="accept-submit"
                    >
                      {{ t('messages.invitations.accept.submit') }}
                    </Button>
                  </div>
                </form>
              </Fluid>
            </template>
          </Card>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
